/**
 * A generic circular buffer implementation.
 * This buffer overwrites old data when it fills up.
 * It's useful for storing the most recent N items efficiently. Currently used for typeahead completion diffs.
 */
export class CircularBuffer<T> {
  private buffer: (T | undefined)[];
  private head: number = 0;
  private size: number = 0;
  private readonly capacity: number;

  constructor(capacity: number) {
    if (capacity <= 0) {
      throw new Error("CircularBuffer capacity must be greater than 0");
    }
    this.capacity = capacity;
    this.buffer = new Array(capacity);
  }

  /**
   * Adds an item to the buffer. If the buffer is full, overwrites the oldest item.
   */
  push(item: T): void {
    this.buffer[this.head] = item;
    this.head = (this.head + 1) % this.capacity;
    if (this.size < this.capacity) {
      this.size++;
    }
  }

  /**
   * Gets the most recently added item, or undefined if the buffer is empty.
   */
  getLast(): T | undefined {
    if (this.size === 0) return undefined;
    const lastIndex = (this.head - 1 + this.capacity) % this.capacity;
    return this.buffer[lastIndex];
  }

  /**
   * Removes and returns the most recently added item, or undefined if the buffer is empty.
   */
  popLast(): T | undefined {
    if (this.size === 0) return undefined;
    const lastIndex = (this.head - 1 + this.capacity) % this.capacity;
    const item = this.buffer[lastIndex];
    this.buffer[lastIndex] = undefined;
    this.head = lastIndex;
    this.size--;
    return item;
  }

  /**
   * Gets the most recent items up to the specified limit, in chronological order.
   *
   * @param limit Maximum number of items to return
   * @returns Array of items in chronological order (oldest first)
   */
  getRecent(limit: number): T[] {
    const items: T[] = [];
    const count = Math.min(limit, this.size);

    for (let i = 0; i < count; i++) {
      const index = (this.head - 1 - i + this.capacity) % this.capacity;
      const item = this.buffer[index];
      if (item !== undefined) {
        items.unshift(item);
      }
    }

    return items;
  }

  /**
   * Clears all items from the buffer.
   */
  clear(): void {
    this.buffer = new Array(this.capacity);
    this.head = 0;
    this.size = 0;
  }

  /**
   * Gets the current number of items in the buffer.
   */
  get currentSize(): number {
    return this.size;
  }

  /**
   * Gets the maximum capacity of the buffer.
   */
  get maxCapacity(): number {
    return this.capacity;
  }

  /**
   * Returns true if the buffer is empty.
   */
  get isEmpty(): boolean {
    return this.size === 0;
  }

  /**
   * Returns true if the buffer is at full capacity.
   */
  get isFull(): boolean {
    return this.size === this.capacity;
  }
}
